import { NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

export interface FilterConfig {
  community?: boolean;
  building?: boolean;
  dateRange?: boolean;
  status?: boolean;
  priority?: boolean;
  period?: boolean;
  /** Show a "Compare to" chip whose options are driven by the selected period grain */
  compareTo?: boolean;
}

@Component({
  selector: 'app-global-filter-panel',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, NgClass, MatIconModule, MatButtonModule, MatMenuModule, MatDatepickerModule, MatNativeDateModule],
  templateUrl: './global-filter-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalFilterPanelComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  @Input() config: FilterConfig = {};
  @Input() defaultPeriod = 'this_year';
  @Output() filtersChanged = new EventEmitter<any>();

  form!: FormGroup;
  associations: any[] = [];
  communitySearch = '';

  private static readonly MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  readonly statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'pending', label: 'Pending' },
    { value: 'closed', label: 'Closed' },
    { value: 'overdue', label: 'Overdue' },
  ];

  readonly priorityOptions = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  readonly periodOptions = [
    { value: 'this_month', label: 'This Month', short: 'Month' },
    { value: 'last_month', label: 'Last Month', short: 'Last Mo.' },
    { value: 'this_quarter', label: 'This Quarter', short: 'Quarter' },
    { value: 'last_quarter', label: 'Last Quarter', short: 'Last Qtr' },
    { value: 'this_year', label: 'This Year', short: 'Year' },
    { value: 'all_time', label: 'All Time', short: 'All Time' },
    { value: 'custom', label: 'Custom', short: 'Custom' },
  ];

  // ── Computed labels ────────────────────────────────────────────────────────

  get filteredAssociations(): any[] {
    const q = this.communitySearch.trim().toLowerCase();
    if (!q) return this.associations;
    return this.associations.filter((a) => a.name?.toLowerCase().includes(q));
  }

  get selectedAssociationName(): string {
    const id = this.form?.get('association_id')?.value;
    if (!id) return 'All';
    return this.associations.find((a) => a.id === id)?.name ?? 'Selected';
  }

  get selectedStatusLabel(): string {
    const v = this.form?.get('status')?.value;
    return v ? (this.statusOptions.find((s) => s.value === v)?.label ?? v) : 'All';
  }

  get selectedPriorityLabel(): string {
    const v = this.form?.get('priority')?.value;
    return v ? (this.priorityOptions.find((p) => p.value === v)?.label ?? v) : 'All';
  }

  /** Dynamic compare-to options based on the current period grain */
  get compareOptions(): { value: string; label: string }[] {
    const period = this.form?.get('period')?.value as string;
    const today = new Date();
    const names = GlobalFilterPanelComponent.MONTH_NAMES;

    if (period === 'this_month' || period === 'last_month') {
      // Previous 18 months, starting from the month before today
      const opts: { value: string; label: string }[] = [];
      let y = today.getFullYear();
      let m = today.getMonth() - 1; // 0-indexed, one month back
      if (m < 0) {
        m = 11;
        y--;
      }
      for (let i = 0; i < 18; i++) {
        opts.push({ value: `${y}-${String(m + 1).padStart(2, '0')}`, label: `${names[m]} ${y}` });
        m--;
        if (m < 0) {
          m = 11;
          y--;
        }
      }
      return opts;
    }

    if (period === 'this_quarter' || period === 'last_quarter') {
      // Previous 12 quarters, starting from the quarter before today
      const opts: { value: string; label: string }[] = [];
      let y = today.getFullYear();
      let q = Math.floor(today.getMonth() / 3) - 1; // 0-indexed quarter, one back
      if (q < 0) {
        q = 3;
        y--;
      }
      for (let i = 0; i < 12; i++) {
        opts.push({ value: `Q${q + 1}-${y}`, label: `Q${q + 1} ${y}` });
        q--;
        if (q < 0) {
          q = 3;
          y--;
        }
      }
      return opts;
    }

    if (period === 'this_year') {
      // Previous 8 calendar years
      const opts: { value: string; label: string }[] = [];
      for (let y = today.getFullYear() - 1; y >= today.getFullYear() - 8; y--) {
        opts.push({ value: `${y}`, label: `${y}` });
      }
      return opts;
    }

    // last_year, all_time, custom → no compare options
    return [];
  }

  get selectedCompareLabel(): string {
    const v = this.form?.get('compare_to')?.value;
    if (!v) return 'None';
    return this.compareOptions.find((o) => o.value === v)?.label ?? v;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.form = this.fb.group({
      association_id: [null],
      date_from: [null],
      date_to: [null],
      status: [null],
      priority: [null],
      period: [this.defaultPeriod],
      compare_to: [null],
    });

    if (this.config.community) {
      this.loadAssociations();
    }

    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntil(this.destroy$)
      )
      .subscribe((val) => this.filtersChanged.emit(this.buildFilters(val)));

    // Emit resolved initial filters immediately so the first data fetch uses real dates
    this.filtersChanged.emit(this.buildFilters(this.form.value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Set a single field value (used by mat-menu items) */
  setField(field: string, value: any): void {
    this.form.patchValue({ [field]: value });
    this.cdr.markForCheck();
  }

  /** Set the active period pill — resets compare_to since the grain changes */
  setPeriod(value: string): void {
    this.form.patchValue({ period: value, date_from: null, date_to: null, compare_to: null });
    this.cdr.markForCheck();
  }

  reset(): void {
    this.form.reset({ period: this.defaultPeriod, compare_to: null });
    this.cdr.markForCheck();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private buildFilters(val: any): any {
    const filters: any = {};
    if (val.association_id) filters.association_id = val.association_id;
    if (val.status) filters.status = val.status;
    if (val.priority) filters.priority = val.priority;
    // Always pass period so the backend can detect all_time and skip date injection
    if (val.period) filters.period = val.period;
    const dates = this.resolvePeriodDates(val.period, val.date_from, val.date_to);
    if (dates.from_date) filters.from_date = dates.from_date;
    if (dates.to_date) filters.to_date = dates.to_date;

    // Compare-to: resolve the selected comparison period to actual dates
    if (val.compare_to) {
      const compare = this.resolveCompareDates(val.compare_to, val.period);
      if (compare.compare_from_date) filters.compare_from_date = compare.compare_from_date;
      if (compare.compare_to_date) filters.compare_to_date = compare.compare_to_date;
    }

    return filters;
  }

  private resolvePeriodDates(period: string, dateFrom: Date | null, dateTo: Date | null): { from_date?: string; to_date?: string } {
    const today = new Date();
    // Use local date parts to avoid UTC conversion shifting dates in UTC+ timezones
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    switch (period) {
      case 'this_month': {
        return { from_date: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to_date: fmt(today) };
      }
      case 'last_month': {
        return {
          from_date: fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
          to_date: fmt(new Date(today.getFullYear(), today.getMonth(), 0)),
        };
      }
      case 'this_quarter': {
        const q = Math.floor(today.getMonth() / 3);
        return { from_date: fmt(new Date(today.getFullYear(), q * 3, 1)), to_date: fmt(today) };
      }
      case 'last_quarter': {
        const q = Math.floor(today.getMonth() / 3);
        const prevQ = q === 0 ? 3 : q - 1;
        const yr = q === 0 ? today.getFullYear() - 1 : today.getFullYear();
        return {
          from_date: fmt(new Date(yr, prevQ * 3, 1)),
          to_date: fmt(new Date(yr, prevQ * 3 + 3, 0)),
        };
      }
      case 'this_year': {
        return { from_date: fmt(new Date(today.getFullYear(), 0, 1)), to_date: fmt(today) };
      }
      case 'all_time':
        return { from_date: '1900-01-01', to_date: fmt(new Date(today.getFullYear() + 5, 11, 31)) };
      case 'custom':
        return {
          from_date: dateFrom ? this.formatDate(dateFrom) : undefined,
          to_date: dateTo ? this.formatDate(dateTo) : undefined,
        };
      default:
        return {};
    }
  }

  /**
   * Resolves a compare_to value (e.g. '2026-04', 'Q1-2026', '2025') to
   * a date range that the backend can use for comparison queries.
   */
  private resolveCompareDates(compareTo: string, period: string): { compare_from_date?: string; compare_to_date?: string } {
    const fmt = (d: Date) => d.toISOString().split('T')[0];

    if (period === 'this_month' || period === 'last_month') {
      // value format: 'YYYY-MM'
      const [y, m] = compareTo.split('-').map(Number);
      return {
        compare_from_date: fmt(new Date(y, m - 1, 1)),
        compare_to_date: fmt(new Date(y, m, 0)), // last day of that month
      };
    }

    if (period === 'this_quarter' || period === 'last_quarter') {
      // value format: 'Q1-2026'
      const [qPart, yPart] = compareTo.split('-');
      const q = parseInt(qPart.replace('Q', ''), 10) - 1; // 0-indexed
      const y = parseInt(yPart, 10);
      return {
        compare_from_date: fmt(new Date(y, q * 3, 1)),
        compare_to_date: fmt(new Date(y, q * 3 + 3, 0)),
      };
    }

    if (period === 'this_year') {
      // value format: 'YYYY'
      const y = parseInt(compareTo, 10);
      return {
        compare_from_date: fmt(new Date(y, 0, 1)),
        compare_to_date: fmt(new Date(y, 11, 31)),
      };
    }

    return {};
  }

  private loadAssociations(): void {
    const params = {
      columns: JSON.stringify(['id', 'name']),
      order: JSON.stringify({ name: 'asc' }),
      limit: '200',
    };
    this.http
      .get<any>('api/association', { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.associations = res.record?.data ?? res.record ?? [];
        this.cdr.markForCheck();
      });
  }

  private formatDate(d: Date): string {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
